import PropTypes from 'prop-types';
import SimpleInput from 'src/components/SimpleInput';
import React, { useState } from 'react';
import { Button, InputNumber, Input, Select } from 'antd';
import { FiBookOpen } from 'react-icons/fi';
import Switcher from 'src/components/Switcher';
import { IntentBlock, IntentField, IntentFieldGroup } from './style';
import VisibilityScopeSettings from './VisibilityScopeSettings';

const VariationDetails = ({
  name,
  clients,
  companyTypes,
  contactTypes,
  parentCompanies,
  topLevels,
  visibilityScope,
  isDefault,
  description,
  isActive,
  timeSaved,
  handleTimeSavedChange,
  handleVariationToggle,
  handleDescriptionChange,
  handleClientsChange,
  handleVisibilityScopeChange,
  handleVariationNameChange,
  handleCompanyTypesChange,
  handleContactTypesChange,
  handleParentCompaniesChange,
  handleTopLevelsChange,
  handleTemplateOpen
}) => {
  const [timeSavedUnit, setTimeSavedUnit] = useState('m');
  const timeSavedAmount = timeSavedUnit === 'm' ? timeSaved : timeSaved / 60;
  return (
    <IntentBlock>
      {isDefault ? (
        <div>
          <IntentFieldGroup>
            <IntentField>
              <p>Describe the intent</p>
              <Input.TextArea
                value={description}
                autoSize={{ minRows: 3, maxRows: 10 }}
                onChange={e => handleDescriptionChange(e.target.value)}
                maxLength={1024}
                showCount
                placeholder="Describe the intent so the agent can recognize it and proceed with next steps. Eg. This intent is for users who are having printer issues"
              />
            </IntentField>
            <IntentField>
              <p />
              <div>
                <Button onClick={handleTemplateOpen} icon={<FiBookOpen />} variant="text">
                  Use template
                </Button>
              </div>
            </IntentField>
            <IntentField>
              <p>Time saved</p>
              <div>
                <InputNumber
                  min={1}
                  style={{
                    maxWidth: 168
                  }}
                  addonAfter={
                    <Select
                      style={{
                        width: 95
                      }}
                      options={[
                        { value: 'm', label: 'minutes' },
                        { value: 'h', label: 'hours' }
                      ]}
                      onChange={unit => {
                        setTimeSavedUnit(unit);
                        handleTimeSavedChange(timeSavedAmount * (unit === 'm' ? 1 : 60));
                      }}
                      defaultValue={timeSavedUnit}
                    />
                  }
                  onChange={value => {
                    handleTimeSavedChange(timeSavedUnit === 'm' ? value : value * 60);
                  }}
                  value={timeSavedAmount}
                />
              </div>
            </IntentField>
            <VisibilityScopeSettings
              clients={clients}
              companyTypes={companyTypes}
              contactTypes={contactTypes}
              parentCompanies={parentCompanies}
              topLevels={topLevels}
              visibilityScope={visibilityScope}
              handleClientsChange={handleClientsChange}
              handleVisibilityScopeChange={handleVisibilityScopeChange}
              handleCompanyTypesChange={handleCompanyTypesChange}
              handleContactTypesChange={handleContactTypesChange}
              handleParentCompaniesChange={handleParentCompaniesChange}
              handleTopLevelsChange={handleTopLevelsChange}
            />
          </IntentFieldGroup>
        </div>
      ) : (
        <IntentFieldGroup>
          <IntentField>
            <p>Name</p>
            <SimpleInput
              value={name}
              onChange={e => handleVariationNameChange(e.target.value)}
              placeholder="Name of the variation"
            />
          </IntentField>
          <IntentField>
            <p>Enabled</p>
            <div>
              <Switcher
                id="variation-status"
                checked={!!isActive}
                onChange={() => {
                  handleVariationToggle(!isActive);
                }}
                controlText={{ checked: 'ON', unchecked: 'OFF' }}
              />
            </div>
          </IntentField>
          <VisibilityScopeSettings
            clients={clients}
            companyTypes={companyTypes}
            contactTypes={contactTypes}
            parentCompanies={parentCompanies}
            topLevels={topLevels}
            visibilityScope={visibilityScope}
            handleClientsChange={handleClientsChange}
            handleVisibilityScopeChange={handleVisibilityScopeChange}
            handleCompanyTypesChange={handleCompanyTypesChange}
            handleContactTypesChange={handleContactTypesChange}
            handleParentCompaniesChange={handleParentCompaniesChange}
            handleTopLevelsChange={handleTopLevelsChange}
            showAllClientsOption={false}
          />
        </IntentFieldGroup>
      )}
    </IntentBlock>
  );
};

VariationDetails.propTypes = {
  name: PropTypes.string.isRequired,
  isDefault: PropTypes.bool.isRequired,
  description: PropTypes.string.isRequired,
  handleDescriptionChange: PropTypes.func.isRequired,
  handleVariationNameChange: PropTypes.func.isRequired,
  handleClientsChange: PropTypes.func.isRequired,
  handleVisibilityScopeChange: PropTypes.func.isRequired,
  handleCompanyTypesChange: PropTypes.func.isRequired,
  handleContactTypesChange: PropTypes.func.isRequired,
  handleParentCompaniesChange: PropTypes.func.isRequired,
  clients: PropTypes.arrayOf(PropTypes.object),
  companyTypes: PropTypes.arrayOf(PropTypes.object),
  contactTypes: PropTypes.arrayOf(PropTypes.object),
  visibilityScope: PropTypes.string,
  timeSaved: PropTypes.number,
  isActive: PropTypes.bool,
  parentCompanies: PropTypes.arrayOf(PropTypes.object),
  topLevels: PropTypes.arrayOf(PropTypes.object),
  handleTopLevelsChange: PropTypes.func.isRequired,
  handleTemplateOpen: PropTypes.func.isRequired,
  handleTimeSavedChange: PropTypes.func.isRequired,
  handleVariationToggle: PropTypes.func.isRequired
};

VariationDetails.defaultProps = {
  clients: [],
  companyTypes: [],
  contactTypes: [],
  visibilityScope: '',
  parentCompanies: [],
  topLevels: [],
  timeSaved: 30,
  isActive: true
};

export default VariationDetails;
